import React from 'react';
import { Button, Col, Dropdown, Flex, Row, Space } from 'antd';
import { DownOutlined, PlusOutlined } from '@ant-design/icons';
import CustomTitle from '../components/CustomTitle';

interface MenuClickInfo {
  key: string;
}

interface Permissions {
  is_create?: boolean;
}

interface DashboardTitleProps {
  title: string;
  showAddButton?: boolean;
  onAddButtonClick?: () => void;
  onBulkUpload?: () => void;
  onBookShipment?: () => void;
  /** Opens the FedEx Quote & Book drawer. Item only renders when supplied. */
  onQuoteShipment?: () => void;
  /** Opens the Create Shell Shipment drawer. Item only renders when supplied. */
  onCreateShellShipment?: () => void;
  /** When true, greys out Book (no QuickStat/WC/Cryoport capability). */
  bookShipmentMenuDisabled?: boolean;
  allPermissions?: Permissions;
  /** When true, include Track / Track in bulk. Default true. */
  showTrack?: boolean;
  /** Optional tab strip rendered inline with the title. */
  tabsSlot?: React.ReactNode;
  switchViewButtons?: React.ReactNode;
  tableView?: boolean;
}

const DashboardTitle: React.FC<DashboardTitleProps> = ({
  title,
  showAddButton = false,
  onAddButtonClick,
  onBulkUpload,
  onBookShipment,
  onQuoteShipment,
  onCreateShellShipment,
  bookShipmentMenuDisabled = false,
  allPermissions,
  showTrack = true,
  tabsSlot,
  switchViewButtons,
  tableView = false,
}) => {
  const items = [
    ...(showTrack
      ? [
          {
            label: 'Track',
            key: 'track',
            children: [
              {
                label: 'Track a shipment',
                key: '1',
              },
              {
                label: 'Track in bulk',
                key: '2',
              },
            ],
          },
        ]
      : []),
    ...(onBookShipment
      ? [
          {
            label: 'Book',
            key: '3',
            disabled: bookShipmentMenuDisabled,
          },
        ]
      : []),
    ...(onQuoteShipment
      ? [
          {
            label: 'Quote',
            key: '4',
          },
        ]
      : []),
    ...(onCreateShellShipment
      ? [
          {
            label: 'Create',
            key: '5',
          },
        ]
      : []),
  ];

  const showNewShipmentButton =
    showAddButton && !!allPermissions?.is_create && items.length > 0;

  const handleMenuClick = (eve: MenuClickInfo): void => {
    if (eve.key === '1') {
      onAddButtonClick?.();
    } else if (eve.key === '2') {
      onBulkUpload?.();
    } else if (eve.key === '3') {
      onBookShipment?.();
    } else if (eve.key === '4') {
      onQuoteShipment?.();
    } else if (eve.key === '5') {
      onCreateShellShipment?.();
    }
  };

  const newShipmentButton = (
    <Dropdown
      menu={{
        items,
        onClick: handleMenuClick,
      }}
      destroyOnHidden
    >
      <Button
        className='custom-track-button'
        style={{
          padding: '6px 10px',
          height: '30px',
          fontSize: '10px',
          fontWeight: '500',
          lineHeight: '16px',
          borderRadius: '6px',
          border: '0',
          backgroundColor: 'var(--light-gray)',
          color: 'var(--text-primary)',
          boxShadow: 'none',
          flexShrink: 0,
          marginLeft: tabsSlot ? 16 : undefined,
          marginBottom: tabsSlot ? 8 : undefined,
        }}
      >
        <Space>
          <PlusOutlined
            style={{
              color: 'var(--text-primary)',
              fontSize: '10px',
              fontWeight: '600',
            }}
          />
          New shipment
          <DownOutlined
            style={{ color: 'var(--text-primary)', fontSize: '10px' }}
          />
        </Space>
      </Button>
    </Dropdown>
  );

  if (tabsSlot) {
    return (
      <div className={`tab-header-mb-20  ${tableView && 'is-table-view'}`}>
        <Flex
          align='center'
          justify='space-between'
          className={`dashboard-title CT-header-title ${!tableView && 'is-list-view'} `}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              flex: 1,
              minWidth: 0,
              overflow: 'visible',
            }}
          >
            <div className='flex-wrapper'>
              <CustomTitle
                className='my-heading'
                text={title}
                fontSize='32px'
                lineHeight='40px'
                fontWeight='700'
                marginBottom={0}
              />
              <div className='sm-d-block'>{switchViewButtons}</div>
            </div>
            <div
              className={tableView && 'sm-d-none'}
              style={{ flex: 1, minWidth: 0, overflow: 'visible' }}
            >
              {tabsSlot}
            </div>
          </div>

          {showNewShipmentButton ? newShipmentButton : null}
        </Flex>
        {tableView && (
          <div className='tab-full-width  '>
            <div className='sm-d-block  Shipment-tabs-sticky'>{tabsSlot}</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <Row gutter={16}>
        <Col span={24}>
          <Flex
            className='dashboard-title'
            wrap
            gap={16}
            justify='space-between'
            align='center'
            style={{ marginBottom: '20px' }}
          >
            <CustomTitle
              className='my-heading'
              text={title}
              fontSize='32px'
              lineHeight='40px'
              fontWeight='700'
              marginBottom={0}
            />

            {showNewShipmentButton ? newShipmentButton : null}
          </Flex>
        </Col>
      </Row>
    </>
  );
};

export default DashboardTitle;
